// PACKAGE #12 Phase E.2 (2026-09-02) — iOS-style top-up bottom sheet for the
// Wallet page specifically. Deliberately a NEW, separate component rather
// than a rework of ../Promotion/Wallet.js: that modal is also opened from
// PromotionDashboard.js, and changing its shape there wasn't asked for and
// wasn't worth the regression risk. Same underlying call
// (POST /api/payments/create, purpose "wallet_topup") as Wallet.js already
// uses — this only changes the UI shell around it, one step instead of two
// (no intermediate "card screen" before the amount picker).
import React, { useState } from "react";
import { getAccessToken } from "../../../awsAuth";
import { coinsToMinor } from "../../../constants/wallet";
import "./TopUpSheet.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const PRESET_AMOUNTS = [100, 300, 1000];

export default function TopUpSheet({ balance, onClose }) {
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const selectedAmount = customAmount ? Number(customAmount) : amount;
  const isValid = Number.isFinite(selectedAmount) && selectedAmount > 0;

  const handlePreset = (value) => {
    setAmount(value);
    setCustomAmount("");
    setError("");
  };

  const handleCustomChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^\d]/g, "");
    setCustomAmount(digitsOnly);
    setAmount(null);
    setError("");
  };

  const handleContinue = async () => {
    if (!isValid || busy) return;
    setBusy(true);
    setError("");

    try {
      const amountMinor = coinsToMinor(selectedAmount);
      const token = await getAccessToken?.().catch(() => null);

      const res = await fetch(`${API_BASE}/api/payments/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          purpose: "wallet_topup",
          amountMinor,
          currency: "UAH",
          description: `Поповнення Cvanto Wallet на ${selectedAmount} ₴`,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.redirectUrl) {
        throw new Error(data?.error || "Не вдалося створити рахунок");
      }

      window.location.href = data.redirectUrl;
    } catch (e) {
      setError(e.message || "Помилка створення платежу");
      setBusy(false);
    }
  };

  return (
    <div className="topUpSheet__overlay" onMouseDown={onClose}>
      <div
        className="topUpSheet"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Поповнити гаманець"
      >
        <div className="topUpSheet__handle" />

        <div className="topUpSheet__header">
          <h2>Поповнити</h2>
          <span className="topUpSheet__balance">Баланс: {balance} Tokens</span>
        </div>

        <div className="topUpSheet__presets">
          {PRESET_AMOUNTS.map((value) => (
            <button
              key={value}
              type="button"
              className={`topUpSheet__chip ${amount === value && !customAmount ? "is-selected" : ""}`}
              onClick={() => handlePreset(value)}
              disabled={busy}
            >
              {value} ₴
            </button>
          ))}
        </div>

        <div className="topUpSheet__custom">
          <input
            type="text"
            inputMode="numeric"
            placeholder="Своя сума"
            value={customAmount}
            onChange={handleCustomChange}
            disabled={busy}
          />
          <span className="topUpSheet__customSuffix">₴</span>
        </div>

        {error && <div className="topUpSheet__error">{error}</div>}

        <button
          type="button"
          className="topUpSheet__cta"
          onClick={handleContinue}
          disabled={!isValid || busy}
        >
          {busy ? "Створюємо рахунок…" : isValid ? `Поповнити на ${selectedAmount} ₴` : "Оберіть суму"}
        </button>

        <p className="topUpSheet__fineprint">1 Token = 1 гривня</p>
      </div>
    </div>
  );
}
